<script lang="ts">
	import {
		FacebookIcon,
		InstagramIcon,
		YouTubeIcon,
		LinkedInIcon,
		TikTokIcon,
		XIcon
	} from "#lib/web-ui/primitives/social-icons/index.js";
	import Section from "#lib/web-ui/primitives/section.svelte";
	import { cloudinaryUrl } from "../template-context.svelte";
	import { titleCase } from "@fergmar/ui/strings";
	let {
		logoUrl,
		name,
		contact,
		bbbBadgeUrl,
		social,
		servicedAreas
	}: {
		logoUrl: string;
		name: string;
		contact: {
			streetAddress: string;
			city: string;
			state: string;
			zipCode: string;
			phone: string;
		};
		bbbBadgeUrl: string;
		social: {
			facebook?: string;
			instagram?: string;
			youtube?: string;
			linkedin?: string;
			tiktok?: string;
			x?: string;
		};
		servicedAreas: { city: string; state: string }[];
	} = $props();

	// Generate footer links from servicedAreas
	const whereWeBuyFooterLinks = servicedAreas.map(({ city, state }) => ({
		label: `Sell My House Fast ${titleCase(city)}, ${state.toUpperCase()}`,
		href: `/${state}/${city}/`
	}));

	const footerMenu = {
		column1: [
			{ label: "Home", href: "/" },
			{ label: "Options", href: "/home-buying-options/" },
			{ label: "Where We Buy", href: "/where-we-buy/" },
			{ label: "About", href: "/about/" },
			{ label: "Meet the Team", href: "/meet-the-team/" },
			{ label: "FAQs", href: "/faqs/" },
			{ label: "Blog", href: "/blog/" },
			{ label: "Contact Us", href: "/contact/" },
			{ label: "Referral Program", href: "/get-paid/" }
		],
		column2: whereWeBuyFooterLinks
	};

	const currentYear = new Date().getFullYear();
</script>

<Section class="bg-foreground/95 flex-1 py-0 text-white">
	<div class="py-12">
		<div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
			<!-- Column 1: Logo & Contact Info -->
			<div class="space-y-4">
				<img
					src={cloudinaryUrl(logoUrl, { width: 240 })}
					alt={name}
					width="240"
					height="90"
					class="mb-4"
				/>
				<address class="text-sm leading-relaxed text-gray-300 not-italic">
					{contact.streetAddress}<br />
					{contact.city}, {contact.state}
					{contact.zipCode}
				</address>
				<a href={`tel:${contact.phone}`} class="block text-sm font-medium hover:text-white">
					{contact.phone}
				</a>
			</div>

			<!-- Column 2: Navigation Links -->
			<div>
				<nav aria-label="Footer navigation" class="space-y-3">
					{#each footerMenu.column1 as link}
						<a href={link.href} class="block text-sm text-gray-300 hover:text-white">
							{link.label}
						</a>
					{/each}
				</nav>
			</div>

			<!-- Column 3: Where We Buy Links -->
			<div>
				<nav aria-label="Footer navigation secondary" class="space-y-3">
					{#each footerMenu.column2 as link}
						<a href={link.href} class="block text-sm text-gray-300 hover:text-white">
							{link.label}
						</a>
					{/each}
				</nav>
			</div>

			<!-- Column 4: BBB Badge & Social Media -->
			<div class="space-y-4">
				<img
					src={cloudinaryUrl(bbbBadgeUrl, { width: 192 })}
					alt="BBB Accredited Business A+ Rating"
					width="192"
					height="53"
					loading="eager"
					fetchpriority="high"
				/>
				<div class="flex gap-4">
					{#if social.facebook}
						<a
							href={social.facebook}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our Facebook page"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<FacebookIcon />
						</a>
					{/if}
					{#if social.instagram}
						<a
							href={social.instagram}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our Instagram page"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<InstagramIcon />
						</a>
					{/if}
					{#if social.youtube}
						<a
							href={social.youtube}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our YouTube channel"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<YouTubeIcon />
						</a>
					{/if}
					{#if social.linkedin}
						<a
							href={social.linkedin}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our LinkedIn page"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<LinkedInIcon />
						</a>
					{/if}
					{#if social.tiktok}
						<a
							href={social.tiktok}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our TikTok page"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<TikTokIcon />
						</a>
					{/if}
					{#if social.x}
						<a
							href={social.x}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit our X page"
							class="text-gray-300 transition-colors hover:text-white"
						>
							<XIcon />
						</a>
					{/if}
				</div>
			</div>
		</div>
	</div>

	<!-- Flex Grow for short pages, tall screens -->
	<div class="flex flex-1"></div>

	<!-- Bottom Bar -->
	<div class="border-t border-neutral-700">
		<div class="max-w-8xl container mx-auto px-4 py-6">
			<div class="flex flex-col gap-2 text-xs text-neutral-400 md:flex-row md:justify-center">
				<div class="space-x-4 text-center">
					<a href="/privacy-policy/" class="hover:text-white">Privacy Policy</a>
					<span>–</span>
					<span>Copyright © {currentYear} {name}. All Rights Reserved.</span>
				</div>
			</div>
		</div>
	</div>
</Section>
